/**
 * Server Error Dialog
 *
 * Displays a user-friendly error message for 5xx server errors
 * with an option to contact support via chatbot
 */

import { useTranslation } from "react-i18next";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { AlertTriangle, MessageCircle, RefreshCw } from "lucide-react";

interface ServerErrorDialogProps {
  open: boolean;
  onClose: () => void;
}

export const ServerErrorDialog = ({
  open,
  onClose,
}: ServerErrorDialogProps) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";

  const handleOpenChatbot = () => {
    // Trigger the Zoho SalesIQ chatbot widget
    if (window.$zoho?.salesiq?.floatwindow?.visible) {
      window.$zoho.salesiq.floatwindow.visible("show");
    } else if (window.$zoho?.salesiq?.chat?.start) {
      window.$zoho.salesiq.chat.start();
    } else {
      // Fallback: try clicking the Zoho widget button directly
      const zohoButton = document.querySelector('[data-id="zsalesiq"]') as HTMLElement
        || document.querySelector('.zsiq_floatmain') as HTMLElement
        || document.querySelector('.siq_bR') as HTMLElement;
      if (zohoButton) {
        zohoButton.click();
      }
    }
    // Close the error dialog after opening chat
    onClose();
  };

  return (
    <AlertDialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <AlertDialogContent dir={isRTL ? "rtl" : "ltr"} className="max-w-md">
        <AlertDialogHeader>
          <div className="flex items-center justify-center mb-4">
            <div className="rounded-full bg-destructive/10 p-3">
              <AlertTriangle className="h-8 w-8 text-destructive" />
            </div>
          </div>
          <AlertDialogTitle className="text-center">
            {t("errors.serverError.title")}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-center">
            {t("errors.serverError.description")}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter className="flex-col sm:flex-row gap-2">
          <Button
            variant="outline"
            onClick={handleOpenChatbot}
            className="w-full sm:w-auto flex items-center gap-2"
          >
            <MessageCircle className="h-4 w-4" />
            {t("errors.serverError.openChat")}
          </Button>
          <AlertDialogAction
            onClick={() => window.location.reload()}
            className="w-full sm:w-auto flex items-center gap-2"
          >
            <RefreshCw className="h-4 w-4" />
            {t("errors.serverError.dismiss")}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};
